// Min-stay policy (owner decision 2026-08-05): quotes are computed and shown
// for any stay length from 1 night — seasonal minStayNights no longer blocks
// availability. The only hard floor is on sending a booking request: at least
// MIN_BOOKING_REQUEST_NIGHTS nights, enforced by the booking CTA (QuoteWidget)
// and server-side in /api/contact.
export const MIN_BOOKING_REQUEST_NIGHTS = 2;

export type Apartment = { listingId: number; title: string; capacity: number; active: boolean; listed: boolean };
export type Season = {
  id: number;
  listingId: number | null;
  name: string;
  dateStart: string;
  dateEnd: string;
  dailyRateUsd: number;
  weeklyRateUsd: number;
  minStayNights: number;
  poolIncludedWeekly: boolean;
  priority: number;
};
export type Fees = {
  listingId: number;
  cleaningUsd: number;
  cleaningWeeklyIncluded: boolean;
  cleaningMinFreeNights: number;
  securityDepositUsd: number;
  extraGuestUsd: number;
  extraGuestThreshold: number;
};
export type BookingRange = { checkin: string; checkout: string };

export type QuoteInput = {
  apartment: Apartment;
  checkin: string;
  checkout: string;
  guests: number;
  seasons: Season[];
  fees: Fees;
  bookings: BookingRange[];
};

export type QuoteResult = {
  available: boolean;
  reason?: 'capacity' | 'dates_booked' | 'invalid_dates' | 'no_pricing';
  nights: number;
  tier: 'daily' | 'weekly';
  nightlyBreakdown: { date: string; seasonName: string; rate: number }[];
  subtotalUsd: number;
  cleaningUsd: number;
  prepaymentUsd: number;
  extraGuestUsd: number;
  totalUsd: number;
  poolIncluded: boolean;
};

function enumerateNights(checkin: string, checkout: string): string[] {
  const out: string[] = [];
  const d = new Date(checkin + 'T00:00:00Z');
  const end = new Date(checkout + 'T00:00:00Z');
  while (d < end) {
    out.push(d.toISOString().slice(0, 10));
    d.setUTCDate(d.getUTCDate() + 1);
  }
  return out;
}

function pickSeason(seasons: Season[], date: string, listingId: number): Season | null {
  const candidates = seasons.filter(
    (s) => s.dateStart <= date && date <= s.dateEnd && (s.listingId === null || s.listingId === listingId),
  );
  if (!candidates.length) return null;
  candidates.sort((a, b) => {
    if (a.listingId !== null && b.listingId === null) return -1;
    if (a.listingId === null && b.listingId !== null) return 1;
    return b.priority - a.priority || a.id - b.id;
  });
  return candidates[0];
}

export function computeQuote(inp: QuoteInput): QuoteResult {
  const empty = {
    nights: 0,
    tier: 'daily' as const,
    nightlyBreakdown: [],
    subtotalUsd: 0,
    cleaningUsd: 0,
    prepaymentUsd: 0,
    extraGuestUsd: 0,
    totalUsd: 0,
    poolIncluded: false,
  };

  if (inp.checkout <= inp.checkin) {
    return { available: false, reason: 'invalid_dates', ...empty };
  }

  if (inp.guests > inp.apartment.capacity) {
    return { available: false, reason: 'capacity', ...empty };
  }

  const nightsList = enumerateNights(inp.checkin, inp.checkout);
  const nights = nightsList.length;

  for (const night of nightsList) {
    if (!pickSeason(inp.seasons, night, inp.apartment.listingId)) {
      return { available: false, reason: 'no_pricing', ...empty, nights };
    }
  }

  const spannedSeasons = nightsList.map(
    (date) => pickSeason(inp.seasons, date, inp.apartment.listingId)!,
  );

  for (const b of inp.bookings) {
    if (inp.checkin < b.checkout && b.checkin < inp.checkout) {
      return { available: false, reason: 'dates_booked', ...empty, nights };
    }
  }

  // Weekly tier was removed 2026-04-24 — always price at the daily rate.
  // `tier` field stays in the return type for backward compatibility of
  // serialized quotes, but only ever reports 'daily'. seasons.weeklyRateUsd
  // and fees.cleaningWeeklyIncluded become inert.
  const tier: 'daily' | 'weekly' = 'daily';
  const nightSeasons = spannedSeasons;
  const nightlyBreakdown = nightSeasons.map((s, i) => ({
    date: nightsList[i],
    seasonName: s.name,
    rate: s.dailyRateUsd,
  }));

  const subtotalUsd = nightlyBreakdown.reduce((sum, n) => sum + n.rate, 0);
  const cleaningUsd =
    inp.fees.cleaningMinFreeNights > 0 && nights >= inp.fees.cleaningMinFreeNights
      ? 0
      : inp.fees.cleaningUsd;
  const extraGuestUsd =
    inp.guests > inp.fees.extraGuestThreshold
      ? (inp.guests - inp.fees.extraGuestThreshold) * inp.fees.extraGuestUsd * nights
      : 0;
  const prepaymentUsd = nightlyBreakdown[0]?.rate ?? 0;
  const totalUsd = subtotalUsd + cleaningUsd + extraGuestUsd;

  // Pool is no longer auto-included by pricing — it has its own economics
  // (entry passes at the pool). poolIncludedWeekly on seasons becomes inert.
  const poolIncluded = false;

  return {
    available: true,
    nights,
    tier,
    nightlyBreakdown,
    subtotalUsd,
    cleaningUsd,
    prepaymentUsd,
    extraGuestUsd,
    totalUsd,
    poolIncluded,
  };
}
